import React, { useEffect, useMemo, useCallback, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import externalServices from '@/services/externalServices';
import styles from './ServicesStatus.module.scss';

const SERVICE_OK = 'UP';
const SERVICE_DOWN = 'DOWN';

type ExternalServiceApi = {
  apiName: string;
  apiUrl: string;
  description: string;
  status: typeof SERVICE_OK | typeof SERVICE_DOWN;
  checkedAt: string;
  lastSuccessAt: string | null;
};

type ExternalServicesResponse = {
  apis: ExternalServiceApi[];
};

const ServicesStatus: React.FC = () => {
  const [extServices, setExtServices] = useState<ExternalServiceApi[]>([]);
  const [selectedApi, setSelectedApi] = useState<string>('');
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const { setLoading } = useGlobalSpinnerContext();

  const groupByApiName = useCallback((items: ExternalServiceApi[]): Record<string, ExternalServiceApi[]> => {
    return items.reduce((acc, item) => {
      const { apiName } = item;
      const group = acc[apiName] ?? [];
      group.push(item);
      acc[apiName] = group;

      return acc;
    }, {});
  }, []);

  const groupsByApiName = useMemo(() => {
    const byName = groupByApiName(extServices);

    return Object.entries(byName).map(([apiName, entries]) => ({ apiName, entries }));
  }, [extServices, groupByApiName]);

  const isMainStatusDown = (entries: ExternalServiceApi[]) => entries.some((entry) => entry.status === SERVICE_DOWN);

  const handleClickGroup = (apiName: string) => setSelectedApi((prev) => (prev === apiName ? '' : apiName));

  useEffect(() => {
    const fetchExtServicesStatus = async () => {
      setLoading(true);
      try {
        const { apis }: ExternalServicesResponse = await externalServices.externalServicesMonitoring();
        setExtServices(apis);
      } catch {
        setExtServices([]);
        addToast(t('errors.externalServicesStatus'), 'ERROR');
      } finally {
        setLoading(false);
      }
    };
    fetchExtServicesStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!groupsByApiName?.length) return null;

  return (
    <>
      <h6>{t('menu.servicesStatus')}</h6>
      <ul className={styles.List}>
        {groupsByApiName.map(({ apiName, entries }) => (
          <Fragment key={apiName}>
            <li
              className={styles.Item}
              onClick={() => handleClickGroup(apiName)}
            >
              <span className={`${styles.StatusDot} ${isMainStatusDown(entries) ? styles.Down : styles.Ok}`} />
              <span className={styles.ServiceName}>{apiName}</span>
              <i className={`di icon-pfeil-chevron-${selectedApi === apiName ? `oben` : `unten`} ${styles.ToggleIcon}`} />
            </li>

            {selectedApi === apiName && entries?.length && (
              <ul className={styles.SubList}>
                {entries?.map((entry) => (
                  <li key={`${apiName}-${entry.apiUrl}`} className={styles.SubItem}>
                    <span className={`${styles.StatusDot} ${entry.status === SERVICE_OK ? styles.Ok : styles.Down}`} />
                    <span className={styles.SubServiceName}>{entry.description}</span>
                  </li>
                ))}
              </ul>
            )}
          </Fragment>
        ))}
      </ul>
    </>
  );
};

export default ServicesStatus;
